import { BehaviorSubject, Subject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { ICmsConfig } from '@icms/core/resource/config/icms-config';
import { IConfigData } from '@df/catalog/config/config.interfaces';
import { NCampaignTracking } from './campaign-tracking.namespace';
import { ActivatedRoute, Params } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import each from 'lodash-es/each';
import isPlainObject from 'lodash-es/isPlainObject';

@Injectable({ providedIn: 'root' })
export class CampaignTrackingService {
    trackingUpdatedSubject = new Subject<void>();

    private _referrer?: string;

    private initiated = false;

    private cmsConfigResource = inject(ICmsConfigResource);

    private activatedRoute = inject(ActivatedRoute);

    private localStorageService = inject(LocalStorageService);

    private expirationTime?: number;

    constructor() {
        this.init();
    }

    getTracking(): { [key: string]: any } {
        try {
            if (this.expirationTime) {
                this.removeExpiredParams(this.expirationTime);
            }
            const params = this.localStorageParams;
            const tracking = {};
            each(params, (value, key) => {
                if (value.value) {
                    tracking[key] = value.value;
                }
            });
            const gaclid = this.getGaclid();
            if (gaclid) {
                tracking['gaclid'] = gaclid;
            }
            return tracking;
        } catch (e) {
            return {};
        }
    }

    get referrer(): string | undefined {
        return this._referrer;
    }

    private async init() {
        this.updateReferrer();
        const expirationTime = await this.loadExpirationTime();
        this.removeExpiredParams(expirationTime);
        this.expirationTime = expirationTime;
        this.activatedRoute.queryParams.subscribe((params: Params) => {
            if (!this.initiated && Object.keys(params || {}).length) {
                this.initiated = true;
                const localStorageParams: NCampaignTracking.ILocalStorageParams = {};
                each(params, (value, key) => {
                    if (NCampaignTracking.PARAMS.indexOf(key) > -1) {
                        localStorageParams[key] = { value, date: Date.now() };
                    }
                });
                this.setLocalStorageParams(localStorageParams);
            }
        });
    }

    private updateReferrer(): void {
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            const referrer = document.referrer;
            if (referrer && referrer.indexOf(window.location.hostname) === -1) {
                this._referrer = referrer;
            }
        }
    }

    private async loadExpirationTime(): Promise<number> {
        try {
            await this.cmsConfigResource.loadModelByUrl(NCampaignTracking.CMS_CONFIG);
        } catch (e) {
        } finally {
            const config = this.cmsConfigResource.getModelByUrl(NCampaignTracking.CMS_CONFIG) as ICmsConfig<
                IConfigData<NCampaignTracking.ICmsConfigDataContent>
            >;
            const configExpiration = config?.data?.content?.expiration;
            const expirationDays = configExpiration && typeof configExpiration === 'number' ? configExpiration : 90;
            return 86400000 * expirationDays;
        }
    }

    private get localStorageParams(): NCampaignTracking.ILocalStorageParams {
        const params = this.localStorageService.get(NCampaignTracking.LOCAL_STORAGE_ID);
        if (isPlainObject(params)) {
            return params;
        }
        return {};
    }

    private setLocalStorageParams(params: NCampaignTracking.ILocalStorageParams) {
        const localStorageParams = this.localStorageParams;
        each(params, (value, key) => {
            if (!localStorageParams[key]) {
                localStorageParams[key] = value;
            }
        });
        this.localStorageService.set(NCampaignTracking.LOCAL_STORAGE_ID, localStorageParams);
        this.trackingUpdatedSubject.next();
    }

    private removeExpiredParams(expirationTime: number): void {
        const params = this.localStorageParams;
        each(params, (value, key) => {
            if (this.shouldBeRemoved(value, expirationTime)) {
                delete params[key];
            }
        });
        this.localStorageService.set(NCampaignTracking.LOCAL_STORAGE_ID, params);
    }

    private shouldBeRemoved(localStorageParam: NCampaignTracking.ILocalStorageParam, expirationTime: number): boolean {
        if (isPlainObject(localStorageParam)) {
            const date = localStorageParam.date;
            if (typeof date === 'number') {
                const diff = Date.now() - date;
                return diff > expirationTime;
            }
        }
        return true;
    }

    private getGaclid(): string | undefined {
        if (typeof document !== 'undefined') {
            const match = document.cookie.match(/_ga=GA\d+\.\d+\.([^;]+)/);
            return match?.[1];
        }
        return undefined;
    }
}
